import type { BimActKc2Data, ContractStatusEnum } from 'gql/graphql';
import { WorkOrderStatusEnum } from 'gql/graphql';
import { formatNumberToRu } from 'utils';
import { formatDateToLocale, getMonthName } from 'utils/formatDateToLocale';

import type { ColDef, ColGroupDef, ValueFormatterParams } from 'ag-grid-community';
import type React from 'react';
import { useMemo } from 'react';

import { getWorkOrderValueFields } from '../../BimWorkOrderPriceTable/hooks';
import { CheckboxCellRendererComponent } from '../components/CheckboxCellRendererComponent';
import { NumberCellRendererComponent } from '../components/NumberCellRendererComponent';
import type { BimActKc2DataExtended } from '../types';

interface IUseBimActsKc2Columns {
    checkedIds: number[];
    setCheckedIds: React.Dispatch<React.SetStateAction<number[]>>;
    setCheckedActForDetails: React.Dispatch<React.SetStateAction<BimActKc2DataExtended | null>>;
    workOrderStatus?: WorkOrderStatusEnum;
    contractStatus?: ContractStatusEnum;
}

const cellClass = (params: any) => (params.data?.isInactive ? 'inactive-act' : '');

export const useBimActsKc2Columns = (props: IUseBimActsKc2Columns) =>
    useMemo<(ColDef<BimActKc2Data> | ColGroupDef<BimActKc2Data>)[]>(
        () => [
            {
                headerName: '',
                field: 'menu' as any,
                width: 80,
                cellRenderer: CheckboxCellRendererComponent,
                cellRendererParams: props,
                cellClass,
            },
            {
                headerName: 'Номер Акта',
                field: 'number',
                width: 270,
                cellRenderer: NumberCellRendererComponent,
                cellClass,
            },
            {
                headerName: 'Ревизия сметы',
                field: 'value.code',
                width: 190,
                cellClass,
            },
            {
                headerName: 'Год Акта',
                field: 'year',
                width: 70,
                cellClass,
            },
            {
                headerName: 'Месяц Акта',
                field: 'month',
                width: 90,
                valueFormatter: (p: ValueFormatterParams<BimActKc2Data>) => getMonthName(p.value),
                cellClass,
            },
            {
                headerName: 'Дата Акта',
                field: 'date',
                width: 115,
                valueFormatter: (p: ValueFormatterParams<BimActKc2Data>) => formatDateToLocale(p.value),
                cellClass,
            },
            {
                headerName: 'Отчетный период',
                children: [
                    {
                        headerName: 'Дата начала',
                        field: 'startDate',
                        width: 150,
                        valueFormatter: (p: ValueFormatterParams<BimActKc2Data>) => formatDateToLocale(p.value),
                        cellClass,
                    },
                    {
                        headerName: 'Дата окончания',
                        field: 'finishDate',
                        width: 150,
                        valueFormatter: (p: ValueFormatterParams<BimActKc2Data>) => formatDateToLocale(p.value),
                        cellClass,
                    },
                ],
            },
            {
                headerName: 'Всего по Акту, в текущем уровне цен',
                field: 'currentPriceTotal',
                width: 120,
                valueFormatter: (p: ValueFormatterParams<BimActKc2Data>) =>
                    p.node?.rowPinned ? '' : formatNumberToRu(p.value, 0),
                cellClass: (params: any) =>
                    params.data?.isInactive ? ['inactive-act', 'ag-right-aligned-cell'] : 'ag-right-aligned-cell',
            },
            ...(getWorkOrderValueFields('value', WorkOrderStatusEnum.InProgress, false) as any),
        ],
        [props],
    );
